import {SORT_FIELD_SEO} from 'modules/search/constants'
import {DEFAULT_SEO_ATTRIBUTES} from 'seo/seo-db/attributes'
import typeCast from 'utils/typeCast'

import {resolveCapabilities} from '@vio/core/src/brand/capabilities/resolveCapabilities'
import {getCugDealsParameter} from '@vio/core/src/offer/business/getCugDealsParameter'
import {numberOfRooms} from '@vio/core/src/room/business/roomConfiguration'
import {getUserTierForSapi} from '@vio/core/src/sapi/utils'
import {getLengthOfStay} from '@vio/core/src/utils/date'
import {removeEmpty} from '@vio/core/src/utils/object'

import {getFiltersFromTaxonomy} from '../searchSeo/getFiltersFromTaxonomy'
import {getBoundingBox} from './utils/boundingBox'
import {getCheckInCheckOutDates} from './utils/dates'
import {getFilters, hasFilters} from './utils/filters'
import {
  makeGetSearchParameter,
  objectFromURLSearchParams
} from './utils/generic'
import {getMetadata} from './utils/metadata'
import {getOriginId} from './utils/originId'
import {getRoomsParams} from './utils/rooms'
import {getSapiLabel} from './utils/sapiLabel'

import type {SearchParameters} from '@findhotel/sapi'
import type {MonetisationCapabilityContext} from '@vio/core/src/brand/capabilities/types/domains'
import type {Brand} from '@vio/core/src/brand/types'
import type {OfferMode} from '@vio/core/src/profile/constants'
import type {SeoAttributes} from 'seo/seo-db/attributes'
import type {GeolocationConfig} from 'types/Localization'

/**
 * User information required to generate search parameters
 */
export interface UserContext {
  isAuthLoading: boolean
  isAuthenticated: boolean
  isMemberPlus: boolean
  isEmployee: boolean
  emailDomain?: string
  userId?: string
}

/**
 * Navigation context that can be composed on server or client
 */
export interface GenerateSearchParametersContext {
  queryParams: URLSearchParams
  cookies: Record<string, string>
  anonymousId: string
  brand: Brand
  geolocation?: GeolocationConfig
  user: UserContext
  useBoVioFirstProfile: boolean
  isAppLockedDealAudience: boolean
  isStickyOtaMode?: boolean
  offerModeOverride?: OfferMode | null
  vclid?: string
  taxonomy?: string | string[] | undefined
  /**
   * SEO attributes for the active locale, read from `state.meta.seoAttributes`.
   * Passed in (rather than looked up here) to keep the preloaded seo-attributes
   * file — and the `fs` read behind it — out of the client bundle.
   */
  seoAttributes?: SeoAttributes
}

const sortFieldMap = {
  price: 'price',
  popularity: 'popularity',
  privatedeals: 'privateDeals',
  distance: 'distance',
  travelhack: 'travelHack',
  guestrating: 'guestRating',
  starrating: 'starRating'
} as const

const normalizeSortField = (
  value: string | undefined
): SearchParameters['sortField'] | undefined => {
  if (!value) return undefined

  const normalized = value.trim().toLowerCase()

  return sortFieldMap[normalized]
}

const normalizeSortOrder = (
  value: string | undefined
): SearchParameters['sortOrder'] | undefined => {
  if (!value) return undefined

  const normalized = value.trim().toLowerCase()

  if (normalized === 'asc' || normalized === 'ascending') return 'ascending'
  if (normalized === 'desc' || normalized === 'descending') return 'descending'

  return undefined
}

/**
 * Generate SAPI search parameters from the navigation context, either on the server or client
 * @remarks
 * - This is the **only** place we should convert url + cookies to SAPI search parameters for the SRP
 * - This function runs on the server AND client, keep it pure!
 */
export const generateSearchParameters = ({
  queryParams,
  cookies,
  anonymousId,
  brand,
  vclid,
  geolocation,
  user: {
    isAuthLoading,
    isAuthenticated,
    isMemberPlus,
    isEmployee,
    emailDomain,
    userId
  },
  useBoVioFirstProfile,
  isAppLockedDealAudience,
  isStickyOtaMode = false,
  offerModeOverride = null,
  taxonomy,
  seoAttributes = DEFAULT_SEO_ATTRIBUTES
}: GenerateSearchParametersContext): SearchParameters => {
  const getParameter = makeGetSearchParameter(queryParams)

  const dateParams = getCheckInCheckOutDates(queryParams)
  const roomsParams = getRoomsParams(queryParams)

  const screenshotsCookie = cookies['screenshotCount']
  const screenshots = screenshotsCookie
    ? typeCast(screenshotsCookie, 'number')
    : 0

  const label = getSapiLabel(queryParams, cookies, anonymousId, brand)
  const clickedOfferRate = getParameter('clickedOfferRate', 'number')

  let preferredRate = clickedOfferRate
  if (!preferredRate) {
    const baseRate = getParameter('baseRate', 'number')
    const taxes = getParameter('taxes', 'number')
    const fees = getParameter('fees', 'number')

    if (
      baseRate &&
      taxes != null &&
      fees != null &&
      roomsParams.rooms &&
      dateParams.checkIn &&
      dateParams.checkOut
    ) {
      const lengthOfStay = getLengthOfStay(
        dateParams.checkIn,
        dateParams.checkOut
      )
      const numRooms = numberOfRooms(roomsParams.rooms)

      if (lengthOfStay > 0 && numRooms > 0) {
        preferredRate = (baseRate + taxes + fees) * lengthOfStay * numRooms
      }
    }
  }

  const metadata = getMetadata(queryParams, cookies, preferredRate, vclid)
  const filters = taxonomy
    ? getFiltersFromTaxonomy(taxonomy, seoAttributes)
    : getFilters(queryParams)

  const originId = getOriginId(
    queryParams,
    useBoVioFirstProfile,
    isStickyOtaMode,
    offerModeOverride
  )
  // SEO SRPs rank by SAPI's tier/score sort so crawlers see tier 1 and 2 hotels first.
  // An explicit sortField in the URL (user picked a sort) still wins.
  const sortField =
    normalizeSortField(getParameter('sortField', 'string')) ??
    (taxonomy ? SORT_FIELD_SEO : undefined)
  const sortOrder = normalizeSortOrder(getParameter('sortOrder', 'string'))
  // We only read `monetisation.offersLockedByDefault` (static), so skip
  // ClientOnly resolvers: Vio's `retention.hasDealFreezeFilter` would
  // otherwise dereference a missing `ctx.experimentVariant` and crash.
  const isBrandOffersLockedByDefault = resolveCapabilities(
    brand.capabilities,
    {} as MonetisationCapabilityContext,
    {skipClientOnly: true}
  ).monetisation.offersLockedByDefault
  const urlParamsObject = objectFromURLSearchParams(queryParams)
  const cugDeals = getCugDealsParameter({
    isMemberPlus,
    isEmployee,
    isAppLockedDealAudience,
    urlParams: urlParamsObject,
    isBrandOffersLockedByDefault
  })
  const tier = getUserTierForSapi({
    isAuthenticated,
    isMemberPlus,
    urlParams: urlParamsObject,
    isBrandOffersLockedByDefault
  })

  const params: SearchParameters = {
    geolocation,
    query: getParameter('query'),
    address: getParameter('address'),
    hotelId: getParameter('hotelId'),
    boundingBox: getBoundingBox(queryParams),
    placeId: getParameter('placeId'),
    ...dateParams,
    ...roomsParams,
    dayDistance: getParameter('checkInDistance', 'number'),
    nights: getParameter('nights', 'number'),
    sortField,
    sortOrder,
    originId,
    label,
    metadata,
    sbc: getParameter('sbc'),
    preferredRate,
    screenshots,
    deviceId: getParameter('deviceId'),
    iataCode: getParameter('iataCode'),
    emailDomain,
    userId,
    cugDeals,
    ...(isAuthLoading ? {} : {tier}), // useSapiSearch waits to search until tier is set
    ...(hasFilters(filters) && {filters}),
    getAllOffers: true
  }

  return removeEmpty(params)
}
